import { cleanObject } from 'commons/utils/objects';
import { getUnmaskedField } from 'commons/utils/formatters';
import { TYPES_FIGURANT } from 'commons/constants/figurants';
import { DocPayload, IFigurantForm } from 'pages/Figurant/types';
import { getService, patchService, postService, putService } from './comfiguraServiceApi';

const documentsField = (items: DocPayload[]) =>
  items
    .filter(item => !!item.content)
    .map(item => ({
      id: item.id || null,
      documentType: item.type.toUpperCase(),
      documentContent: getUnmaskedField(item.content),
    }));

const createFigurantBody = ({
  name,
  socialName,
  genre,
  birthDate,
  phone,
  identity,
  identityDocumentId,
  cpf,
  cpfDocumentId,
  email,
  drt,
  drtDocumentId,
  agency,
  cep,
  address,
  addressNumber,
  complement,
  neighborhood,
  city,
  state,
  plateia,
  adultoProfissional,
  figuranteSp,
}: IFigurantForm) => ({
  name,
  socialName,
  genre,
  birthDate,
  phone: getUnmaskedField(phone),
  agencyId: agency,
  email,
  documents: documentsField([
    { type: 'cpf', content: cpf, id: cpfDocumentId },
    { type: 'rg', content: identity, id: identityDocumentId },
    { type: 'drt', content: drt, id: drtDocumentId },
  ]),
  isActive: true,
  cep,
  address,
  addressNumber,
  complement,
  neighborhood,
  city,
  state,
  plateia,
  adultoProfissional,
  figuranteSp,
});
export const createFigurant = (values: IFigurantForm) => {
  return postService('/background-actor', cleanObject(createFigurantBody(values)));
};

const createForeignerBody = ({
  name,
  socialName,
  genre,
  birthDate,
  nationality,
  passport,
  passportDocumentId,
  email,
  drt,
  drtDocumentId,
  agency,
  phone,
  cep,
  address,
  addressNumber,
  complement,
  neighborhood,
  city,
  state,
  plateia,
  adultoProfissional,
  figuranteSp,
}: IFigurantForm) => ({
  name,
  socialName,
  genre,
  birthDate,
  nationality,
  agencyId: agency,
  email,
  documents: documentsField([
    { type: 'passport', content: passport, id: passportDocumentId },
    { type: 'drt', content: drt, id: drtDocumentId },
  ]),
  isActive: true,
  phone: getUnmaskedField(phone),
  cep,
  address,
  addressNumber,
  complement,
  neighborhood,
  city,
  state,
  plateia,
  adultoProfissional,
  figuranteSp,
});
export const createForeigner = (values: IFigurantForm) => {
  return postService('/foreigner', cleanObject(createForeignerBody(values)));
};

const createMinorBody = ({
  fatherName,
  motherName,
  guardianName,
  cpfGuardian,
  schoolEntranceTime,
  schoolLeavingTime,
  name,
  cpf,
  cpfDocumentId,
  email,
  drt,
  drtDocumentId,
  genre,
  birthDate,
  phone,
  agency,
  cep,
  address,
  addressNumber,
  complement,
  neighborhood,
  city,
  state,
}: IFigurantForm) => ({
  name,
  genre,
  birthDate,
  phone: getUnmaskedField(phone),
  isActive: true,
  email,
  agencyId: agency,
  documents: documentsField([
    { type: 'cpf', content: cpf, id: cpfDocumentId },
    { type: 'drt', content: drt, id: drtDocumentId },
  ]),
  minor: {
    fatherName,
    motherName,
    cpfGuardian,
    guardianName,
    schoolEntranceTime,
    schoolLeavingTime,
  },
  cep,
  address,
  addressNumber,
  complement,
  neighborhood,
  city,
  state,
});
export const createMinor = (values: IFigurantForm) => {
  return postService('/minor', cleanObject(createMinorBody(values)));
};

const createUnemancipatedMinorBody = ({
  fatherName,
  motherName,
  guardianName,
  cpf,
  cpfDocumentId,
  email,
  cpfGuardian,
  schoolEntranceTime,
  schoolLeavingTime,
  name,
  genre,
  birthDate,
  phone,
  drt,
  drtDocumentId,
  numberBirthCertificate,
  ufBirthCertificate,
  pageBirthCertificate,
  bookBirthCertificate,
  dateBirthCertificate,
  agency,
  cep,
  address,
  addressNumber,
  complement,
  neighborhood,
  city,
  state,
}: IFigurantForm) => ({
  name,
  genre,
  birthDate,
  phone: getUnmaskedField(phone),
  isActive: true,
  agencyId: agency,
  email,
  documents: documentsField([
    { type: 'cpf', content: cpf, id: cpfDocumentId },
    { type: 'drt', content: drt, id: drtDocumentId },
  ]),
  minor: {
    fatherName,
    motherName,
    cpfGuardian: getUnmaskedField(cpfGuardian),
    guardianName,
    schoolEntranceTime,
    schoolLeavingTime,
    unemancipatedMinor: {
      numberBirthCertificate,
      ufBirthCertificate,
      pageBirthCertificate,
      bookBirthCertificate,
      dateBirthCertificate,
    },
  },
  cep,
  address,
  addressNumber,
  complement,
  neighborhood,
  city,
  state,
});
export const UnemancipatedMinor = (values: IFigurantForm) => {
  return postService('/unemancipated-minor', cleanObject(createUnemancipatedMinorBody(values)));
};

const addPhysicalCharacteristicsBody = ({
  name,
  socialName,
  genre,
  birthDate,
  nationality,
  phone,
  cpf,
  email,
  drt,
  figurantType,
  identity,
  passport,
  skills,
  figurantId,
  bodyExposure,
  makeChanges,
  ...rest
}: IFigurantForm) => ({ ...rest, bodyExposure: bodyExposure === 'true', makeChanges: makeChanges === 'true' });

export const addPhysicalCharacteristics = (figurantId: number, values: IFigurantForm) => {
  return postService(`physical-characteristics/${figurantId}`, cleanObject(addPhysicalCharacteristicsBody(values)));
};

export const addSkills = (figurantId: number, values: IFigurantForm['skills']) => {
  return postService(
    `attachments/${figurantId}`,
    values.filter(item => item.disabled || item.isEditing).map(({ isEditing, disabled, ...item }) => item)
  );
};

export const getFigurant = ({ type, id }: { type: string; id: string }) => {
  const pathByType: Record<string, string> = {
    [TYPES_FIGURANT.FIGURANTE]: 'background-actor',
    [TYPES_FIGURANT.MENOR]: 'unemancipated-minor',
    [TYPES_FIGURANT.MENOR_EMANCIPADO]: 'minor',
    [TYPES_FIGURANT.ESTRANGEIRO]: 'foreigner',
  };

  if (type) {
    const path = pathByType[type];

    if (!path) {
      throw new Error(`Tipo de figurante inválido: ${type}`);
    }

    return getService(`${path}/${id}`);
  }

  return getService(`background-actor/${id}`);
};

export const updateFigurant = (values: IFigurantForm) => {
  const path = {
    [TYPES_FIGURANT.FIGURANTE]: 'background-actor',
    [TYPES_FIGURANT.MENOR]: 'unemancipated-minor',
    [TYPES_FIGURANT.MENOR_EMANCIPADO]: 'minor',
    [TYPES_FIGURANT.ESTRANGEIRO]: 'foreigner',
  }[values.figurantType];

  const body = {
    [TYPES_FIGURANT.FIGURANTE]: {
      ...createFigurantBody({ ...values, backgroundActorAttachments: null } as any),
      nationality: 'Brasileiro',
    },
    [TYPES_FIGURANT.MENOR]: { ...createUnemancipatedMinorBody(values), nationality: 'Brasileiro' },
    [TYPES_FIGURANT.MENOR_EMANCIPADO]: { ...createMinorBody(values), nationality: 'Brasileiro' },
    [TYPES_FIGURANT.ESTRANGEIRO]: createForeignerBody(values),
  }[values.figurantType];

  return putService(`${path}/${values.figurantId}`, body);
};

interface IChangeStatusEntity {
  status: 'activate' | 'deactivate';
  payload: {
    entityId: number;
    description: string;
    entityType: string;
  };
}

export const changeStatusEntity = ({ status, payload }: IChangeStatusEntity) => {
  return postService(`${status}Entity`, payload);
};

export const approveFigurant = async ({
  id,
  producerEmail,
  approvedByProducer,
}: {
  id: number;
  producerEmail: string;
  approvedByProducer: number;
}) => {
  return patchService('/approve-reject-order', {
    id,
    producerEmail,
    approvedByProducer,
  });
};
